import { computed, inject, Injectable } from '@angular/core';

import { ClassStore } from '../../stores/class.store';

/**
 * Per-class product-variant gate, centralised per
 * `.migration-intel/cross-cutting/hidden-behaviors-and-anti-patterns.md §20.3`:
 *
 *   "Build a FeatureFlagsService.isILit20() / .isWTW() / .isMyELD() —
 *    DO NOT scatter `strStartsWith(productCode, 'ilit20')` everywhere."
 *
 * The Angular app is shipped to multiple iLit product variants at runtime
 * (the class-picker does not deployment-filter on `productCode`). Features
 * that legacy gates on a productCode prefix mirror that gate in Angular —
 * see `cv-bottom-nav.component.ts:171`, `performance.constants.ts:98-100`, and
 * `assignments-benchmark-assessment-content.service.ts`.
 *
 * The target product for this app is **iLit 4.5**, so
 * this service exposes only the predicate that has a current consumer:
 * `isIlit45`. Additional predicates (`isIlit20`, `isMyEld`, `isWtw`, etc.)
 * should be added here when — and only when — a real consumer needs them.
 * Do not add speculative helpers.
 */

/**
 * Pure string predicate. Use this from mappers / pure helpers / services
 * that already have a `productCode` string in hand and should not inject.
 *
 * Accepts the bare `'45'` form (some legacy `productGradeID`-style fields
 * carry that value) and any `ilit45*` prefix (case-insensitive, whitespace-
 * tolerant). The bare-form rule is exercised by this service's co-located spec.
 */
export function isIlit45ProductCode(value: string | null | undefined): boolean {
  const normalized = value?.trim().toLowerCase() ?? '';
  return normalized === '45' || normalized.startsWith('ilit45');
}

export function isIlit20ProductCode(value: string | null | undefined): boolean {
  const normalized = value?.trim().toLowerCase() ?? '';
  return normalized.startsWith('ilit20');
}

export function isMyEldProductCode(value: string | null | undefined): boolean {
  const normalized = value?.trim().toLowerCase() ?? '';
  return normalized === 'myeld';
}

/**
 * Signal-backed wrapper. Inject this from components / facades that should
 * react to class changes via `ClassStore.productCode()`.
 */
@Injectable({ providedIn: 'root' })
export class ProductVariantService {
  private readonly classStore = inject(ClassStore);

  readonly isIlit45 = computed(() => isIlit45ProductCode(this.classStore.productCode()));
  readonly isIlit20 = computed(() => isIlit20ProductCode(this.classStore.productCode()));
  readonly isMyEld = computed(() => isMyEldProductCode(this.classStore.productCode()));
}
